import type { FlatConfig } from 'domain/index';
import { isSkuAvailable } from 'constants/quarterConfig';
import { getAvailableEncodingSchemes } from 'utils/configUtilities.jsx';
import { getVisibleOptions, findCurrentMapping } from 'utils/productFilters';
import { dualCpuFlashEligible, dualCpuFlashIneligibleReason } from 'domain/rules/dualCpuRules';
import { mlecEligible, mlecIneligibleReason, mlecHddLossLabel } from 'domain/rules/mlecRules';
import { FLASH_NODE_MODELS } from 'utils/partNumbers.jsx';
import { DUAL_CPU_FLASH_FEATURE_ENABLED } from 'utils/dualCpuFlashFeature';
import type { StorageVariant } from './storageSetSummary';

export type Mapping = { partNumber: string; capacity: number; displayName: string; availableFrom?: string; adminOnly?: boolean };
export type FilterCtx = { hasSpecialAccess: boolean; quarter: string };

export const BASE_MIN_NODES = 3;
export const EXPANSION_MIN_NODES = 1;

/** Pure option resolution for both storage sets — no React, so R14's split
 *  moves these branches (and their share of the old hook's cyclomatic
 *  complexity) out of any component/hook body. Every list is filtered by
 *  quarter and access (§1.6 rule 2); callers pass the already-computed
 *  `filterCtx` rather than re-deriving it here. */

export function resolveSsdOptions(
  variant: StorageVariant,
  pMap: Record<string, any>,
  filterCtx: FilterCtx,
  config: FlatConfig,
): { ssdOptions: Mapping[]; currentSsd: Mapping | null } {
  const flash = variant === 'flash';
  const ssdOptions: Mapping[] = getVisibleOptions(flash ? pMap.afaSsd : pMap.hybridSsd, filterCtx);
  const currentSsd = findCurrentMapping(
    ssdOptions,
    (flash ? config.afaSsdPartNumber : config.hybridSsdPartNumber) ?? '',
    flash ? config.afaVpodSsdCapacity : config.hybridVpodSsdCapacity,
  ) as Mapping | null;
  return { ssdOptions, currentSsd };
}

export function resolveJbodAndHdd(
  caps: Record<string, any>,
  pMap: Record<string, any>,
  filterCtx: FilterCtx,
  config: FlatConfig,
): { jbodOptions: { value: string; label: string }[]; hddOptions: Mapping[]; currentHdd: Mapping | null } {
  const jbodSizes: number[] = (caps.availableJbodSizes ?? []).filter((size: number) => {
    const mappings = pMap.hybridHdd?.[size] ?? [];
    return mappings.some((m: Mapping) => !m.availableFrom || isSkuAvailable(m.availableFrom, config.quarter));
  });
  const jbodOptions = jbodSizes.map((size) => ({
    value: String(size),
    label: caps.jbodSizeLabels?.[size] ?? `${size} drives`,
  }));
  const hddOptions: Mapping[] = getVisibleOptions(pMap.hybridHdd?.[config.jbodSize], filterCtx);
  const currentHdd = findCurrentMapping(hddOptions, config.hybridHddPartNumber ?? '', config.hybridHddCapacity) as Mapping | null;
  return { jbodOptions, hddOptions, currentHdd };
}

export function resolveEncoding(
  variant: StorageVariant,
  config: FlatConfig,
  enabled: boolean,
  nodeCount: number,
): { encodingOptions: string[]; encodingValue: string; constraintNote: string | null } {
  const flash = variant === 'flash';
  const encodingOptions: string[] = getAvailableEncodingSchemes(nodeCount > 0 ? nodeCount : BASE_MIN_NODES);
  const encodingValue = (flash ? config.afaEncodingScheme : config.hybridEncodingScheme) as string;
  // domain/rules resets a scheme the node count can no longer serve
  // (storageRules.applyEncodingReconcile); surface that rather than let it be silent.
  // BaseSystem only, like the rule itself (rules/core.ts runs R1 inside
  // `!isExpansion`): an expansion has no encoding control and nothing reconciles
  // its stored scheme, so there the mismatch is routine and the note — "has been
  // reset" — would simply be untrue.
  const isExpansion = config.systemType === 'Expansion';
  const constraintNote = enabled && !isExpansion && !encodingOptions.includes(encodingValue)
    ? `Encoding options follow node count — ${encodingOptions.length} available at ${nodeCount} nodes; the previous scheme is no longer valid and has been reset.`
    : null;
  return { encodingOptions, encodingValue, constraintNote };
}

/** The enabled/nodeCount/minNodes side of the split — kept
 *  separate from resolveVariantOptions below purely to stay under the
 *  complexity budget; there is no other reason for the split. */
export function resolveVariantBasics(
  variant: StorageVariant,
  config: FlatConfig,
): { isExpansion: boolean; enabled: boolean; nodeCount: number; minNodes: number } {
  const flash = variant === 'flash';
  const isExpansion = config.systemType === 'Expansion';
  const enabled = Boolean(flash ? config.afaEnabled : config.hybridEnabled);
  const nodeCount = (flash ? config.afaNodeCount : config.hybridNodeCount) ?? 0;
  const minNodes = isExpansion ? EXPANSION_MIN_NODES : BASE_MIN_NODES;
  return { isExpansion, enabled, nodeCount, minNodes };
}

/** Spares/compression/E2E field selection — the other half of the split
 *  described above. */
export function resolveVariantOptions(
  variant: StorageVariant,
  config: FlatConfig,
  caps: Record<string, any>,
): { sparesOptions: number[]; sparesValue: string; compressionOptions: number[]; compressionValue: string; e2e: boolean } {
  const flash = variant === 'flash';
  const sparesOptions: number[] = (config.availableSpares ?? caps.availableSpares ?? []) as number[];
  const sparesValue = String((flash ? config.afaSpares : config.hybridSpares) ?? 2);
  const compressionOptions: number[] = (caps.compressionRatios ?? []) as number[];
  const compressionValue = String((flash ? config.afaCompressionRatio : config.hybridCompressionRatio) ?? 1.0);
  const e2e = Boolean(flash ? config.afaEndToEndEncryption : config.hybridEndToEndEncryption);
  return { sparesOptions, sparesValue, compressionOptions, compressionValue, e2e };
}

export type NodeModelOption = { value: 'standard' | 'dualCpu'; label: string };

/** CONF-210: the Flash Node Model control. dualCpuAccess-only (CONF-259):
 *  hidden unless the user can edit it or the quote already carries the Dual CPU
 *  node (then shown locked, so anyone else can see what they are quoting). Locked with the
 *  vendor's reason when the vendor cannot supply the node — the value shown is
 *  what the config says, and R13 will already have reverted it. With the kill
 *  switch off the feature is pulled: no locked control is offered for it, and
 *  only a quote already carrying the node still shows it, locked. */
export function resolveNodeModel(
  variant: StorageVariant,
  config: FlatConfig,
  canEdit: boolean,
): {
  showNodeModel: boolean;
  nodeModelValue: 'standard' | 'dualCpu';
  nodeModelOptions: NodeModelOption[];
  nodeModelLocked: boolean;
  nodeModelReason: string | null;
} {
  const flash = variant === 'flash';
  const nodeModelValue = config.afaNodeModel === 'dualCpu' ? 'dualCpu' : 'standard';
  const showNodeModel = flash && (nodeModelValue === 'dualCpu' || (canEdit && DUAL_CPU_FLASH_FEATURE_ENABLED));
  const vendorReason = dualCpuFlashIneligibleReason(config.hardwareVendor);
  const eligible = dualCpuFlashEligible(config.hardwareVendor);
  const nodeModelLocked = !canEdit || !eligible;
  const nodeModelReason = !canEdit ? 'Requires Dual CPU access' : vendorReason;
  const nodeModelOptions: NodeModelOption[] = [
    { value: 'standard', label: `Single CPU (${FLASH_NODE_MODELS.standard.partNumber})` },
    { value: 'dualCpu', label: `Dual CPU (${FLASH_NODE_MODELS.dualCpu.partNumber})` },
  ];
  return { showNodeModel, nodeModelValue, nodeModelOptions, nodeModelLocked, nodeModelReason };
}

/** minRawPb / sizeBy / showAdvanced — the last of the composing hook's
 *  field selection, split out for the same reason. */
export function resolveSizingSummary(
  variant: StorageVariant,
  config: FlatConfig,
  isExpansion: boolean,
): { minRawPb: string; sizeBy: 'nodeCount' | 'capacity'; showAdvanced: boolean } {
  const flash = variant === 'flash';
  const minRawTb = (flash ? config.afaMinRawCapacity : config.hybridMinRawCapacity) ?? 0;
  const minRawPb = minRawTb === 0 ? '' : String(minRawTb / 1000);
  const sizeBy: 'nodeCount' | 'capacity' = config.sizeByNodeCount ? 'nodeCount' : 'capacity';
  return { minRawPb, sizeBy, showAdvanced: !isExpansion };
}

/** CONF-94. MLEC lives on the Capacity set only, on Base AND Expansion — the
 *  classic ExpansionConfigCard shows it even though it hides encoding and
 *  spares. Eligibility and the locked-reason copy come from mlecRules, the
 *  single definition shared with R12 and the classic MlecToggle. */
export function resolveMlec(
  variant: StorageVariant,
  config: FlatConfig,
  vendorId: string,
): { showMlec: boolean; mlecOn: boolean; mlecLocked: boolean; mlecReason: string | null; mlecLoss: string | null } {
  // Keyed on the active vendor's id, as R12 is (rules/context.ts), so the
  // switch can never offer a state the rule would immediately clear.
  return {
    showMlec: variant !== 'flash',
    mlecOn: Boolean(config.hybridMLEC),
    mlecLocked: !mlecEligible(vendorId, config.jbodSize),
    mlecReason: mlecIneligibleReason(vendorId, config.jbodSize),
    // CONF-220: what MLEC costs, shown whether it is on or off; null when locked.
    mlecLoss: mlecHddLossLabel(vendorId, config.jbodSize),
  };
}
